import { useState } from 'react';
import { BookingStatus } from '../types';
import { bookFlight } from '../utils/api';

interface BookingButtonProps {
  isValid: boolean;
  bookingData: {
    flight: import('../types').Flight | null;
    passengers: import('../types').Passenger[];
    departureDate: string;
    returnDate: string | null;
    totalPrice: number;
  };
  onSuccess: (confirmationId: string) => void;
}

export function BookingButton({ isValid, bookingData, onSuccess }: BookingButtonProps) {
  const [status, setStatus] = useState<BookingStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleBooking = async () => {
    if (!isValid || !bookingData.flight) return;

    setStatus('loading');
    setError(null);

    try {
      const result = await bookFlight({
        flight: bookingData.flight,
        passengers: bookingData.passengers,
        departureDate: bookingData.departureDate,
        returnDate: bookingData.returnDate,
        userData: null,
        totalPrice: bookingData.totalPrice,
        specialRequests: '',
      });

      if (result.success) {
        setStatus('success');
        onSuccess(result.confirmationId);
      } else {
        setStatus('error');
        setError('Booking failed. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setError('Network error. Check connection and retry.');
    }
  };

  const getButtonText = () => {
    switch (status) {
      case 'loading':
        return 'PROCESSING...';
      case 'success':
        return 'CONFIRMED ✓';
      case 'error':
        return 'RETRY';
      default:
        return 'INITIATE BOOKING';
    }
  };

  const getButtonClass = () => {
    const base = 'w-full py-4 font-mono text-sm uppercase tracking-widest transition-all duration-300 border-2';
    switch (status) {
      case 'loading':
        return `${base} border-orbital-yellow text-orbital-yellow bg-orbital-yellow/10 animate-pulse`;
      case 'success':
        return `${base} border-orbital-cyan text-orbital-cyan bg-orbital-cyan/10 shadow-[0_0_30px_rgba(0,255,255,0.3)]`;
      case 'error':
        return `${base} border-red-500 text-red-500 bg-red-500/10`;
      default:
        return `${base} border-orbital-primary text-orbital-primary hover:bg-orbital-primary/20 hover:shadow-[0_0_20px_rgba(0,212,255,0.4)]`;
    }
  };

  return (
    <div className="w-full">
      <button
        onClick={handleBooking}
        disabled={!isValid || status === 'loading'}
        className={getButtonClass()}
      >
        {status === 'loading' && (
          <span className="inline-block animate-spin mr-2">◴</span>
        )}
        {getButtonText()}
      </button>

      {/* Error message */}
      {status === 'error' && error && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/50 text-red-400 text-sm font-mono">
          [ERROR] {error}
        </div>
      )}

      {/* Success message */}
      {status === 'success' && (
        <div className="mt-3 p-3 bg-orbital-cyan/10 border border-orbital-cyan/50 text-orbital-cyan text-sm font-mono">
          [SUCCESS] Booking confirmed. Confirmation ID sent to email.
        </div>
      )}

      {/* Terminal status line */}
      <div className="mt-4 text-xs text-gray-600 font-mono text-center">
        <span className="text-orbital-magenta">&gt;</span> SYSTEM STATUS: {status.toUpperCase()}
        {!isValid && <span className="text-yellow-500 ml-2">INCOMPLETE FIELDS</span>}
      </div>
    </div>
  );
}
